import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessagesSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { Modal, TextField, SelectField } from "@/components/Modal";

function NewRoomModal({ open, onClose, onCreated }) {
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [name, setName] = useState("");
  const [inviteConnections, setInviteConnections] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) TurfTalk.games().then((r) => { const g = r.games || []; setGames(g); if (g[0]) setGameId(g[0].id); }).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!gameId || !name.trim()) { toast.error("Pick a game and enter a name."); return; }
    setBusy(true);
    try {
      const r = await TurfTalk.createPrivateRoom({ game_id: gameId, name: name.trim(), invite_connections: inviteConnections, invite_user_ids: [] });
      toast.success("Private room created");
      onCreated?.(r.room || r);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New private room" testid="new-room-modal">
      <SelectField label="Game" value={gameId} onChange={(e) => setGameId(e.target.value)} data-testid="room-game">
        {games.map((g) => <option key={g.id} value={g.id}>{`${g.away.abbr} @ ${g.home.abbr} · ${g.league} WK${g.week}`}</option>)}
      </SelectField>
      <TextField label="Room name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chiefs Diehards" data-testid="room-name" maxLength={60} />
      <label className="flex items-center gap-2 text-sm text-white/70 mb-4 cursor-pointer">
        <input type="checkbox" checked={inviteConnections} onChange={(e) => setInviteConnections(e.target.checked)} className="accent-[#2F7D32]" data-testid="room-invite-connections" />
        Invite my connections
      </label>
      <button onClick={submit} disabled={busy} data-testid="room-create-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium disabled:opacity-50">{busy ? "Creating…" : "Create room"}</button>
    </Modal>
  );
}

export default function RoomsPage() {
  const [data, setData] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();
  const load = () => TurfTalk.roomsSummary().then(setData).catch(() => setData({ rooms: [] }));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Rooms" subtitle="Official game rooms and your private rooms." right={
        <button onClick={() => setShowNew(true)} data-testid="new-room-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white text-sm font-medium"><Plus size={16} /> New private room</button>
      } />
      {!data ? <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
        : (data.rooms || []).length === 0 ? <Empty title="No active rooms" detail="Join an official room from any game page, or create a private room." icon={MessagesSquare} />
        : (
          <div className="space-y-3">
            {data.rooms.map((r) => (
              <Link key={r.id} to={`/room/${r.id}`} data-testid={`room-${r.id}`} className="flex items-center gap-4 tt-panel rounded-xl p-4 hover:border-[#3A4859] transition-colors">
                <div className="w-11 h-11 rounded-lg bg-[#24362A] grid place-items-center text-[#4ea152]"><MessagesSquare size={20} /></div>
                <div className="flex-1 min-w-0"><div className="text-white font-medium truncate">{r.name}</div><div className="text-[11px] text-white/40 uppercase tracking-wide">{r.kind} room{r.muted ? " · muted" : ""}</div></div>
              </Link>
            ))}
          </div>
        )}
      <NewRoomModal open={showNew} onClose={() => setShowNew(false)} onCreated={(room) => { setShowNew(false); load(); if (room?.id) navigate(`/room/${room.id}`); }} />
    </div>
  );
}
